import type { ThreadQuery } from '@zuruflex/api-client';
import { slaState, type SlaTargets } from '@zuruflex/core';
import {
  THREAD_CHANNELS,
  THREAD_PRIORITIES,
  THREAD_STATUSES,
  THREAD_TOPICS,
  type MessageThread,
  type ThreadChannel,
  type ThreadKind,
  type ThreadPriority,
  type ThreadStatus,
  type ThreadTopic,
} from '@zuruflex/types';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * The desk's filters, held in the URL rather than in `useState`.
 *
 * A view is a link an agent pastes into a handover note — "here is the
 * unassigned payment queue, it is yours until five" — and a tile on the
 * dashboard is the same link with the numbers on it. Neither is possible
 * while the filters live in component state, and both are free once they
 * do not. The selected conversation moves to the path (`/inbox/:id`) for
 * the same reason, which is what the partner portal already does.
 */

/**
 * The five saved views, in the order they are read.
 *
 * They are *not* a sixth facet. Each one answers a different question an
 * agent asks at a different moment of the day — what is there at all, what
 * is mine, what has nobody picked up, what did I put down, and what is
 * about to be late — and three of them cannot be spelled as a facet at all:
 * `mine` and `unassigned` are the `assignee` parameter, and `breaching` is
 * a clock rather than a column.
 */
export const INBOX_VIEWS = ['all', 'mine', 'unassigned', 'snoozed', 'breaching'] as const;
export type InboxView = (typeof INBOX_VIEWS)[number];

/** How many rows one page of the queue holds. */
export const INBOX_PER_PAGE = 50;

export interface InboxFilters {
  /**
   * SUPPORT or DIRECT. A different axis from the views — one is whose
   * conversation this is, the others are where it stands — which is why it
   * stays its own tab strip above them.
   */
  kind: ThreadKind;
  view: InboxView;
  /**
   * Repeatable, because `GET /v1/threads` takes it repeated: OPEN *and*
   * WAITING is the default desk and would be a choice between two things
   * that both need answering if only one were allowed.
   */
  status: ThreadStatus[];
  /**
   * Single, because the API takes one. A checkbox facet that silently
   * dropped the second pick — or narrowed page one and left page two
   * alone — would be a filter that lies, so these three are chosen rather
   * than ticked.
   */
  topic: ThreadTopic | null;
  priority: ThreadPriority | null;
  channel: ThreadChannel | null;
  /** A staff id. `me` and `none` arrive through the views instead. */
  assignee: string | null;
  q: string;
  page: number;
}

const DEFAULTS: InboxFilters = {
  kind: 'SUPPORT',
  view: 'all',
  status: [],
  topic: null,
  priority: null,
  channel: null,
  assignee: null,
  q: '',
  page: 1,
};

function oneOf<T extends string>(allowed: readonly T[], raw: string | null): T | null {
  return raw && (allowed as readonly string[]).includes(raw) ? (raw as T) : null;
}

function readFilters(params: URLSearchParams): InboxFilters {
  const page = Number(params.get('page'));

  return {
    kind: params.get('kind') === 'DIRECT' ? 'DIRECT' : 'SUPPORT',
    view: oneOf(INBOX_VIEWS, params.get('view')) ?? DEFAULTS.view,
    // Comma-separated rather than repeated, so the whole desk is one
    // readable query string somebody can eyeball before pasting it.
    status: (params.get('status') ?? '')
      .split(',')
      .map((value) => oneOf(THREAD_STATUSES, value))
      .filter((value): value is ThreadStatus => value !== null),
    topic: oneOf(THREAD_TOPICS, params.get('topic')),
    priority: oneOf(THREAD_PRIORITIES, params.get('priority')),
    channel: oneOf(THREAD_CHANNELS, params.get('channel')),
    assignee: params.get('assignee') || null,
    q: params.get('q') ?? '',
    page: Number.isFinite(page) && page > 1 ? Math.floor(page) : 1,
  };
}

function writeFilters(filters: InboxFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.kind !== DEFAULTS.kind) params.set('kind', filters.kind);
  if (filters.view !== DEFAULTS.view) params.set('view', filters.view);
  if (filters.status.length > 0) params.set('status', filters.status.join(','));
  if (filters.topic) params.set('topic', filters.topic);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.channel) params.set('channel', filters.channel);
  if (filters.assignee) params.set('assignee', filters.assignee);
  if (filters.q.trim()) params.set('q', filters.q.trim());
  if (filters.page > 1) params.set('page', String(filters.page));
  return params;
}

export interface InboxFiltersHandle {
  filters: InboxFilters;
  /**
   * Merges a change in. Everything but `page` itself resets the page —
   * being left on page four of a queue that now has one page is an empty
   * list that looks like an empty inbox.
   */
  set: (patch: Partial<InboxFilters>) => void;
  reset: () => void;
  /** The current query string, for a link that keeps the desk it was opened from. */
  search: string;
  /**
   * Everything but the page, as one string. What a selection is cleared
   * on: a tick that survives a filter change is a bulk action about to be
   * applied to rows the agent can no longer see.
   */
  selectionKey: string;
}

export function useInboxFilters(): InboxFiltersHandle {
  const [params, setParams] = useSearchParams();

  const filters = useMemo(() => readFilters(params), [params]);

  const set = useCallback(
    (patch: Partial<InboxFilters>) => {
      const next: InboxFilters = {
        ...filters,
        ...patch,
        page: 'page' in patch ? (patch.page ?? 1) : 1,
      };
      // `replace`, so a morning of narrowing a queue does not bury the page
      // the agent arrived from under forty back-button presses.
      setParams(writeFilters(next), { replace: true });
    },
    [filters, setParams],
  );

  const reset = useCallback(() => {
    setParams(writeFilters({ ...DEFAULTS, kind: filters.kind }), { replace: true });
  }, [filters.kind, setParams]);

  const search = writeFilters(filters).toString();
  const selectionKey = writeFilters({ ...filters, page: 1 }).toString();

  return { filters, set, reset, search: search ? `?${search}` : '', selectionKey };
}

/**
 * What a view forces on top of the facets.
 *
 * `mine` and `unassigned` are the `assignee` parameter under a name an
 * agent recognises. `snoozed` is the one place SNOOZED is asked for, since
 * it is deliberately not in the default desk. `breaching` asks for the two
 * live statuses and is narrowed afterwards — see `narrowToView`.
 */
function viewQuery(view: InboxView): Partial<ThreadQuery> {
  switch (view) {
    case 'mine':
      return { assignee: 'me' };
    case 'unassigned':
      return { assignee: 'none' };
    case 'snoozed':
      return { status: ['SNOOZED'] };
    case 'breaching':
      return { status: ['OPEN', 'WAITING'] };
    default:
      return {};
  }
}

/** True when the view owns the assignee parameter, so the facet must not fight it. */
export function viewOwnsAssignee(view: InboxView): boolean {
  return view === 'mine' || view === 'unassigned';
}

/** True when the view owns the status parameter. */
export function viewOwnsStatus(view: InboxView): boolean {
  return view === 'snoozed' || view === 'breaching';
}

export function toThreadQuery(filters: InboxFilters): ThreadQuery {
  const forced = viewQuery(filters.view);

  return {
    kind: filters.kind,
    page: filters.page,
    perPage: INBOX_PER_PAGE,
    ...(filters.status.length > 0 && !viewOwnsStatus(filters.view)
      ? { status: filters.status }
      : {}),
    ...(filters.topic ? { topic: filters.topic } : {}),
    ...(filters.priority ? { priority: filters.priority } : {}),
    ...(filters.channel ? { channel: filters.channel } : {}),
    ...(filters.assignee && !viewOwnsAssignee(filters.view) ? { assignee: filters.assignee } : {}),
    ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
    ...forced,
  };
}

/**
 * The one narrowing the API cannot do.
 *
 * "Past target" is a clock read against the operator's two numbers, not a
 * column, so there is nothing to ask `GET /v1/threads` for. It is applied
 * to the page in hand and to nothing else, which is honest about what it
 * is: the breaching threads *on this page*, not a count of every late
 * thread on the desk. That number is the reporting page's job.
 *
 * Without targets — the metrics call has not landed, or failed — nothing
 * is hidden. A view that empties itself because a number is missing reads
 * as "no thread is late", which is the one wrong answer.
 */
export function narrowToView(
  items: MessageThread[],
  view: InboxView,
  targets: SlaTargets | null,
  now: Date,
): MessageThread[] {
  if (view !== 'breaching' || !targets) return items;
  return items.filter((thread) => slaState(thread, targets, now) === 'breached');
}

/** Which facets are set, for the row of removable badges under the rail. */
export function activeFacets(filters: InboxFilters): Array<{
  key: 'status' | 'topic' | 'priority' | 'channel' | 'assignee' | 'q';
  value: string;
  clear: Partial<InboxFilters>;
}> {
  const chips: ReturnType<typeof activeFacets> = [];

  if (!viewOwnsStatus(filters.view)) {
    for (const status of filters.status) {
      chips.push({
        key: 'status',
        value: status,
        clear: { status: filters.status.filter((other) => other !== status) },
      });
    }
  }
  if (filters.topic) chips.push({ key: 'topic', value: filters.topic, clear: { topic: null } });
  if (filters.priority) {
    chips.push({ key: 'priority', value: filters.priority, clear: { priority: null } });
  }
  if (filters.channel) {
    chips.push({ key: 'channel', value: filters.channel, clear: { channel: null } });
  }
  if (filters.assignee && !viewOwnsAssignee(filters.view)) {
    chips.push({ key: 'assignee', value: filters.assignee, clear: { assignee: null } });
  }
  if (filters.q.trim()) chips.push({ key: 'q', value: filters.q.trim(), clear: { q: '' } });

  return chips;
}
